import { SupportedCurrency } from '@/@types/Enums/supported-currency';
import Loader from '@/components/Loader/Loader';
import InfoArticle from '@/components/Marketplace/info-article';
import InfoBlock from '@/components/Marketplace/info-block';
import { BackButton } from '@/components/shared/BackButton/back-button';
import BadgeStatus from '@/components/shared/Badge/Badge';
import Divider from '@/components/shared/Divider/Divider';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from '@/hooks/use-toast';
import { usePermissions } from '@/hooks/usePermissions';
import LayoutInside from '@/layout/LayoutInside';
import { AffiliateProgramService } from '@/services/affiliate-program.service';
import {
  MarketingService,
  type MarketplaceCheckout,
  type MarketplaceOffer,
  type MarketplaceProductLink,
} from '@/services/marketplace.service';
import useModalStore from '@/store/useModalStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy, ExternalLink, Link, List, Loader2, Mail, Tags } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

export default function MarketingDetailsPage() {
  const { t } = useTranslation([
    'marketing',
    'common',
    'affiliates',
    'invitationAffiliateLink',
  ]);
  const { hasPermission } = usePermissions();
  const canRequestAffiliation = hasPermission(
    'marketplace.request_marketplace_affiliation',
  );
  const { id } = useParams<{ id: string }>();
  const { modals, closeModal, openModal } = useModalStore();
  const navigation = useNavigate();
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const handleCopyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLinkId(id);
      setTimeout(() => setCopiedLinkId(null), 2000);
      toast({
        title: t('common:copied'),
        description: t('common:linkCopied'),
      });
    } catch (err) {
      toast({
        title: t('common:error'),
        description: t('common:errorCopying'),
        variant: 'destructive',
      });
    }
  };

  const { data: details, isPending } = useQuery({
    queryKey: [`marketing-${id}`],
    queryFn: async () => await MarketingService.productHighlightsDetails(id || ''),
    enabled: !!id,
    staleTime: 1000 * 60 * 60 * 24,
  });

  const items = [
    { text: t('common:marketing'), link: '/marketplace' },
    { text: t('common:details'), link: '/marketplace/details' },
  ];

  const productInfo = [
    {
      label: t('common:productType'),
      value:
        details?.product?.category?.type &&
        t(`common:${details?.product?.category?.type}`)
          ? t(`common:${details?.product?.category?.type}`)
          : '---',
    },
    {
      label: t('common:category'),
      value: details?.product?.category?.name || '---',
    },
    {
      label: t('affiliates:choose_option'),
      value: details?.product?.product_affiliate_program?.attribution
        ? t(
            `affiliates:${details?.product?.product_affiliate_program?.attribution}`
          )
        : '---',
    },
    {
      label: t('common:approvation'),
      value: details?.invitation_status
        ? t(`common:${details?.invitation_status}`)
        : '---',
    },
    {
      label: t('affiliates:cookie_duration'),
      value: details?.product?.product_affiliate_program?.cookie_duration
        ? `${details?.product?.product_affiliate_program?.cookie_duration} ${t(
            'invitationAffiliateLink:days'
          )}`
        : '---',
    },
  ];

  const affiliateSupportInfo = [
    {
      label: t('common:email'),
      value:
        details?.product?.product_affiliate_program?.contact_email ||
        details?.product?.email_support ||
        '---',
    },
    {
      label: t('common:phone'),
      value:
        details?.product?.phone_prefix && details?.product?.whatsapp_support
          ? `${details?.product?.phone_prefix} ${details?.product?.whatsapp_support}`
          : details?.product?.whatsapp_support
          ? details.product.whatsapp_support
          : '---',
    },
    {
      label: t('common:detailsAffiliatePage'),
      value: details?.product?.product_affiliate_program?.page_url ? (
        <a
          className='inline-flex items-center gap-2 text-interactive hover:underline transition-colors'
          target='_blank'
          rel='noopener noreferrer'
          href={details?.product?.product_affiliate_program?.page_url}
        >
          <span className='truncate max-w-[200px]'>
            {details?.product?.product_affiliate_program?.page_url}
          </span>
          <ExternalLink size={14} className='flex-shrink-0' />
        </a>
      ) : (
        '---'
      ),
    },
  ];

  const headers = [
    {
      title: t('common:name_single'),
      key: 'name',
    },
    {
      title: t('common:value'),
      key: 'value',
    },
    {
      title: t('common:receiveUpTo'),
      key: 'receive',
    },
  ];

  const headersOffers = [
    { title: t('common:offer'), key: 'offer' },
    { title: t('common:link'), key: 'link' },
  ];

  function getInitials(name: any): React.ReactNode | Iterable<React.ReactNode> {
    const parts = name?.split(' ');
    const firstInitial = parts?.[0][0];
    const lastInitial = parts?.length > 1 ? parts[parts.length - 1][0] : '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  }

  const { mutate, isPending: isRequestingAffiliation } = useMutation({
    mutationFn: async () =>
      await AffiliateProgramService.applyAffiliateProgram({
        productId: id ?? '',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`marketing-${id}`] });
      closeModal('requestAffiliation');
    },
    onError: (error: any) => {
      toast({
        title: t('common:server_error'),
        description:
          error?.response?.data?.message || t('common:errorTryAgain'),
        variant: 'destructive',
      });
    },
  });

  const handleRequestAffiliation = () => {
    mutate();
  };

  const isProductOwner = details?.relationship === 'producer';
  const isAffiliate = details?.relationship === 'affiliate';
  const isPendingInvitation = details?.invitation_status === 'pending';
  return (
    <LayoutInside items={items}>
      {isPending ? (
        <Loader fullScreen />
      ) : (
        <section className='pb-8'>
          <BackButton />
          <div className='flex w-full justify-between gap-6 max-xl:flex-col mt-6'>
            <Card className='bg-background h-fit border-elevated w-full border p-6 shadow-none md:mt-0 xl:w-[328px]'>
              <div className='mb-6 flex items-center gap-4 md:flex-col md:items-start'>
                <Avatar className='w-16 h-16 md:w-20 md:h-20'>
                  <AvatarImage
                    src={details?.product?.images?.[0]?.url}
                    alt={details?.product?.name || 'Product Image'}
                  />
                  <AvatarFallback className='text-lg'>
                    {getInitials(details?.product?.name || '---')}
                  </AvatarFallback>
                </Avatar>
                <article className='flex-1'>
                  <h2 className='text-foreground text-base font-semibold mb-1'>
                    {details?.product?.name || '-'}
                  </h2>
                  <p className='text-muted text-sm'>
                    {t('common:by')} {details?.seller_name || '-'}
                  </p>
                </article>
              </div>

              {details?.relationship === 'producer' && (
                <div className='mb-6'>
                  <BadgeStatus
                    text={t(`invitationAffiliateLink:producer`)}
                    status={'active'}
                  />
                </div>
              )}

              <Divider className='my-6' />

              <div className='flex flex-col flex-shrink-0'>
                <p className='text-muted text-xs'>{t('common:receiveUpTo')}</p>
                <div className='flex flex-col gap-2'>
                  {Object.values(SupportedCurrency).every(
                    (currency) =>
                      !details?.offers_grouped?.[currency] ||
                      !details?.offers_grouped?.[currency][0]?.commission
                        ?.value_formatted
                  ) ? (
                    <p className='text-base text-interactive font-medium'>
                      {t('common:noCommissionAvailable')}
                    </p>
                  ) : (
                    <>
                      {Object.values(SupportedCurrency).map((currency) => {
                        const commission =
                          details?.offers_grouped?.[currency]?.[0]?.commission
                            ?.value_formatted;
                        return (
                          commission && (
                            <p
                              key={currency}
                              className='text-xl font-medium text-interactive'
                            >
                              {commission}
                            </p>
                          )
                        );
                      })}
                    </>
                  )}
                </div>
              </div>

              {(isAffiliate || isProductOwner) && !isPendingInvitation ? (
                <Button
                  variant='outline'
                  className='w-full mt-4'
                  onClick={() =>
                    navigation(
                      `/products/edit-product/${details?.product?.uuid}/geral`
                    )
                  }
                >
                  {t('common:viewProduct')}
                </Button>
              ) : null}

              {details?.invitation_status === 'pending' && (
                <div className='mt-4'>
                  <BadgeStatus
                    text={t(`common:pending_request`)}
                    status={details?.invitation_status}
                  />
                </div>
              )}

              {!isAffiliate && !isProductOwner && canRequestAffiliation && (
                <Dialog
                  open={modals.requestAffiliation}
                  onOpenChange={(open) => {
                    if (open) {
                      openModal('requestAffiliation');
                    } else {
                      closeModal('requestAffiliation');
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button
                      variant='default'
                      className='w-full mt-4'
                      onClick={() => openModal('requestAffiliation')}
                    >
                      {t('common:requestAffiliation')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className='max-w-[520px]'>
                    <DialogHeader className='space-y-2'>
                      <DialogTitle className='text-xl font-semibold'>
                        {t('common:requestAffiliation')}?
                      </DialogTitle>
                      <DialogDescription className='text-sm text-muted-foreground'>
                        {t('common:requestAffiliationDescription')}
                      </DialogDescription>
                    </DialogHeader>
                    <div className='py-4'>
                      <Alert variant='warning' className='border-warning/50'>
                        <AlertTriangle
                          className='h-5 w-5 text-warning flex-shrink-0'
                        />
                        <div className='space-y-4'>
                          <AlertTitle className='text-warning font-semibold text-sm mb-3'>
                            {t('common:prohibitedActions') ||
                              'Ações proibidas na Mundpay'}
                          </AlertTitle>
                          <AlertDescription className='space-y-3'>
                            <div className='space-y-1'>
                              <p className='font-semibold text-foreground text-sm'>
                                {t('common:requestAffiliationAlert1')}:
                              </p>
                              <p className='text-sm text-muted-foreground leading-relaxed'>
                                {t('common:requestAffiliationAlert1Description')}
                              </p>
                            </div>
                            <div className='space-y-1'>
                              <p className='font-semibold text-foreground text-sm'>
                                {t('common:requestAffiliationAlert2')}:
                              </p>
                              <p className='text-sm text-muted-foreground leading-relaxed'>
                                {t('common:requestAffiliationAlert2Description')}
                              </p>
                            </div>
                            <div className='space-y-1'>
                              <p className='font-semibold text-foreground text-sm'>
                                {t('common:requestAffiliationAlert3')}:
                              </p>
                              <p className='text-sm text-muted-foreground leading-relaxed'>
                                {t('common:requestAffiliationAlert3Description')}
                              </p>
                            </div>
                          </AlertDescription>
                        </div>
                      </Alert>
                    </div>
                    <DialogFooter className='flex-row gap-2 sm:gap-0'>
                      <Button
                        type='button'
                        variant='outline'
                        onClick={() => closeModal('requestAffiliation')}
                        className='flex-1 sm:flex-initial'
                      >
                        {t('common:cancel')}
                      </Button>
                      <Button
                        disabled={isRequestingAffiliation}
                        variant='default'
                        onClick={handleRequestAffiliation}
                        className='flex-1 sm:flex-initial'
                      >
                          {isRequestingAffiliation
                            && <Loader2 className='animate-spin' />}
                          {t('common:confirmRequest')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </Card>

            <section className='w-full xl:max-w-[594px] space-y-8'>
              {(isProductOwner || isAffiliate) && !isPendingInvitation ? (
                <div>
                  <div className='flex items-center gap-2 mb-4'>
                    <Link size={18} className='text-muted' />
                    <h2 className='text-foreground font-semibold text-lg'>
                      {t('common:yourLinks')}
                    </h2>
                  </div>
                  <Card className='overflow-hidden w-full border-border'>
                    {!details?.links || details?.links?.length === 0 ? (
                      <div className='p-8 text-center text-muted'>
                        {t('links:emptyLinks.title')}
                      </div>
                    ) : (
                      <div className='overflow-x-auto'>
                        <Table className='w-full'>
                          <TableHeader className='bg-[#18181b]'>
                            <TableRow className='hover:bg-transparent border-b border-b-colorBorder'>
                              {headersOffers.map((header) => (
                                <TableCell
                                  key={header.key}
                                  className='h-12 px-4 text-xs font-semibold text-muted uppercase'
                                >
                                  {header.title}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {details?.links?.map((link: MarketplaceProductLink) => (
                              <TableRow
                                key={link.id}
                                className='h-16 border-b border-b-colorBorder hover:bg-muted/5 transition-colors'
                                data-cy='link-table-row'
                              >
                                <TableCell className='font-medium px-4 py-3'>
                                  <p className='text-foreground'>{link.name}</p>
                                </TableCell>
                                <TableCell className='px-4 py-3'>
                                  <div className='flex items-center gap-2'>
                                    <a
                                      href={link?.url}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      className='flex items-center gap-2 bg-[#27272a] hover:bg-[#3f3f46] text-interactive px-3 py-2 rounded-md transition-colors flex-1 min-w-0'
                                    >
                                      <span className='truncate text-sm'>
                                        {link?.url}
                                      </span>
                                      <ExternalLink
                                        size={14}
                                        className='flex-shrink-0'
                                      />
                                    </a>
                                    <Button
                                      variant='ghost'
                                      size='sm'
                                      className='p-2 h-8 w-8'
                                      onClick={() =>
                                        handleCopyToClipboard(link?.url, link.id)
                                      }
                                    >
                                      {copiedLinkId === link.id ? (
                                        <Check
                                          size={16}
                                          className='text-green-500'
                                        />
                                      ) : (
                                        <Copy size={16} />
                                      )}
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </Card>
                </div>
              ) : null}

              {(isProductOwner || isAffiliate) && !isPendingInvitation ? (
                <div>
                  <div className='flex items-center gap-2 mb-4'>
                    <Link size={18} className='text-muted' />
                    <h2 className='text-foreground font-semibold text-lg'>
                      {t('common:yourCheckouts')}
                    </h2>
                  </div>
                  <Card className='overflow-hidden w-full border-border'>
                    {!details?.checkouts || details?.checkouts?.length === 0 ? (
                      <div className='p-8 text-center text-muted text-sm'>
                        {t('common:noCheckouts') || 'Nenhum checkout encontrado'}
                      </div>
                    ) : (
                      <div className='overflow-x-auto'>
                        <Table className='w-full'>
                          <TableHeader className='bg-[#18181b]'>
                            <TableRow className='hover:bg-transparent border-b border-b-colorBorder'>
                              {headersOffers.map((header) => (
                                <TableCell
                                  key={header.key}
                                  className='h-12 px-4 text-xs font-semibold text-muted uppercase'
                                >
                                  {header.title}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {details?.checkouts?.map((checkout: MarketplaceCheckout) => (
                              <TableRow
                                key={checkout.id}
                                className='h-16 border-b border-b-colorBorder hover:bg-muted/5 transition-colors'
                                data-cy='checkout-table-row'
                              >
                                <TableCell className='font-medium px-4 py-3'>
                                  <div className='flex flex-col gap-1'>
                                    <p className='text-foreground'>
                                      {checkout.name}
                                    </p>
                                    <p className='text-sm text-muted'>
                                      {checkout.price_formatted}
                                    </p>
                                  </div>
                                </TableCell>
                                <TableCell className='px-4 py-3'>
                                  <div className='flex items-center gap-2'>
                                    <a
                                      href={checkout?.url}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      className='flex items-center gap-2 bg-[#27272a] hover:bg-[#3f3f46] text-interactive px-3 py-2 rounded-md transition-colors flex-1 min-w-0'
                                    >
                                      <span className='truncate text-sm'>
                                        {checkout?.url}
                                      </span>
                                      <ExternalLink
                                        size={14}
                                        className='flex-shrink-0'
                                      />
                                    </a>
                                    <Button
                                      variant='ghost'
                                      size='sm'
                                      className='p-2 h-8 w-8'
                                      onClick={() =>
                                        handleCopyToClipboard(
                                          checkout?.url,
                                          checkout.id
                                        )
                                      }
                                    >
                                      {copiedLinkId === checkout.id ? (
                                        <Check
                                          size={16}
                                          className='text-green-500'
                                        />
                                      ) : (
                                        <Copy size={16} />
                                      )}
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </Card>
                </div>
              ) : null}

              <section className='space-y-6'>
                <InfoBlock
                  icon={<List size={18} className='text-muted' />}
                  title={t('common:sellerInfo')}
                >
                  {productInfo.map((info, idx) => (
                    <InfoArticle
                      key={idx}
                      label={info.label}
                      value={info.value}
                    />
                  ))}
                </InfoBlock>
                <article className='w-full pt-4'>
                  <p className='text-sm font-medium mb-3 text-muted'>
                    {t('affiliates:description')}
                  </p>
                  <p
                    className='text-sm text-foreground leading-relaxed max-w-full overflow-hidden line-clamp-6'
                    title={
                      details?.product?.product_affiliate_program?.description ||
                      details?.product?.description
                    }
                  >
                    {details?.product?.product_affiliate_program?.description ||
                      details?.product?.description ||
                      '---'}
                  </p>
                </article>
              </section>

              <Divider className='my-8' />

              <section className='space-y-6'>
                <InfoBlock
                  icon={<Mail size={18} className='text-muted' />}
                  title={t('common:affiliateSupport')}
                >
                  {affiliateSupportInfo.map((info, idx) => (
                    <InfoArticle
                      key={idx}
                      label={info.label}
                      value={info.value}
                    />
                  ))}
                </InfoBlock>
              </section>

              {!isAffiliate &&
                !isProductOwner &&
                (!details?.invitation_status ||
                  details?.invitation_status === 'pending') && (
                  <section className='space-y-6'>
                    <div className='flex items-center gap-2 mb-4'>
                      <Tags size={18} className='text-muted' />
                      <h2 className='text-foreground font-semibold text-lg'>
                        {t('common:offers')}
                      </h2>
                    </div>

                    {(details?.offers_grouped['BRL']?.length ?? 0) > 0 && (
                      <div className='space-y-4'>
                        <h3 className='text-interactive text-xl font-semibold'>
                          BRL
                        </h3>
                        <Card className='overflow-hidden border-border'>
                          <div className='overflow-x-auto'>
                            <Table className='w-full'>
                              <TableHeader className='bg-[#18181b]'>
                                <TableRow className='hover:bg-transparent border-b border-b-colorBorder'>
                                  {headers.map((header) => (
                                    <TableCell
                                      key={header.key}
                                      className='h-12 px-4 text-xs font-semibold text-muted uppercase'
                                    >
                                      {header.title}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {details?.offers_grouped['BRL']?.map((offer: MarketplaceOffer) => (
                                  <TableRow
                                    key={offer.id}
                                    className='h-16 border-b border-b-colorBorder hover:bg-muted/5 transition-colors'
                                    data-cy='offer-table-row'
                                  >
                                    <TableCell className='font-medium px-4 py-3 text-foreground'>
                                      {offer.name}
                                    </TableCell>
                                    <TableCell className='px-4 py-3 text-foreground'>
                                      {offer.price_formatted}
                                    </TableCell>
                                    <TableCell className='px-4 py-3 text-interactive font-medium'>
                                      {offer?.commission?.value_formatted}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </Card>
                      </div>
                    )}

                    {(details?.offers_grouped['USD']?.length ?? 0) > 0 && (
                      <div className='space-y-4'>
                        <h3 className='text-interactive text-xl font-semibold'>
                          USD
                        </h3>
                        <Card className='overflow-hidden border-border'>
                          <div className='overflow-x-auto'>
                            <Table className='w-full'>
                              <TableHeader className='bg-[#18181b]'>
                                <TableRow className='hover:bg-transparent border-b border-b-colorBorder'>
                                  {headers.map((header) => (
                                    <TableCell
                                      key={header.key}
                                      className='h-12 px-4 text-xs font-semibold text-muted uppercase'
                                    >
                                      {header.title}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {details?.offers_grouped['USD']?.map((offer: MarketplaceOffer) => (
                                  <TableRow
                                    key={offer.id}
                                    className='h-16 border-b border-b-colorBorder hover:bg-muted/5 transition-colors'
                                    data-cy='offer-table-row'
                                  >
                                    <TableCell className='font-medium px-4 py-3 text-foreground'>
                                      {offer.name}
                                    </TableCell>
                                    <TableCell className='px-4 py-3 text-foreground'>
                                      {offer.price_formatted}
                                    </TableCell>
                                    <TableCell className='px-4 py-3 text-interactive font-medium'>
                                      {offer?.commission?.value_formatted}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </Card>
                      </div>
                    )}

                    {(details?.offers_grouped['EUR']?.length ?? 0) > 0 && (
                      <div className='space-y-4'>
                        <h3 className='text-interactive text-xl font-semibold'>
                          EUR
                        </h3>
                        <Card className='overflow-hidden border-border'>
                          <div className='overflow-x-auto'>
                            <Table className='w-full'>
                              <TableHeader className='bg-[#18181b]'>
                                <TableRow className='hover:bg-transparent border-b border-b-colorBorder'>
                                  {headers.map((header) => (
                                    <TableCell
                                      key={header.key}
                                      className='h-12 px-4 text-xs font-semibold text-muted uppercase'
                                    >
                                      {header.title}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {details?.offers_grouped['EUR']?.map((offer: MarketplaceOffer) => (
                                  <TableRow
                                    key={offer.id}
                                    className='h-16 border-b border-b-colorBorder hover:bg-muted/5 transition-colors'
                                    data-cy='offer-table-row'
                                  >
                                    <TableCell className='font-medium px-4 py-3 text-foreground'>
                                      {offer.name}
                                    </TableCell>
                                    <TableCell className='px-4 py-3 text-foreground'>
                                      {offer.price_formatted}
                                    </TableCell>
                                    <TableCell className='px-4 py-3 text-interactive font-medium'>
                                      {offer?.commission?.value_formatted}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </Card>
                      </div>
                    )}
                  </section>
                )}
            </section>
          </div>
        </section>
      )}
    </LayoutInside>
  );
}
